import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BRAND } from "@/data/site";
import { ClientTracking } from "@/components/admin/ClientTracking";
import { BlogManager } from "@/components/admin/BlogManager";
import { ResourcesManager } from "@/components/admin/ResourcesManager";
import { TestimonialsManager } from "@/components/admin/TestimonialsManager";
import { CategoriesManager } from "@/components/admin/CategoriesManager";
import { BookingsManager } from "@/components/admin/BookingsManager";
import { AnalyticsDashboard } from "@/components/admin/AnalyticsDashboard";
import { CaseStudiesManager } from "@/components/admin/CaseStudiesManager";
import {
  ChartPieSlice, Users, Package, CalendarCheck, DownloadSimple, Article, FileText,
  SignOut, ShieldCheck, Buildings, Boat, Star, SquaresFour, ChartLineUp, Briefcase, WhatsappLogo,
} from "@phosphor-icons/react";

const NAV = [
  { id: "overview", label: "Overview", icon: ChartPieSlice },
  { id: "analytics", label: "Analytics", icon: ChartLineUp },
  { id: "leads", label: "Leads", icon: Users },
  { id: "quotes", label: "Quote Requests", icon: Package },
  { id: "consultations", label: "Consultations", icon: CalendarCheck },
  { id: "bookings", label: "Bookings", icon: CalendarCheck },
  { id: "supplier", label: "Supplier Checks", icon: ShieldCheck },
  { id: "audits", label: "Factory Audits", icon: Buildings },
  { id: "clients", label: "Clients & Tracking", icon: Boat },
  { id: "downloads", label: "Downloads", icon: DownloadSimple },
  { id: "blog", label: "Blog Posts", icon: Article },
  { id: "casestudies", label: "Case Studies", icon: Briefcase },
  { id: "resources", label: "Resources", icon: FileText },
  { id: "testimonials", label: "Testimonials", icon: Star },
  { id: "categories", label: "Categories", icon: SquaresFour },
];

const LEAD_STATUS = ["new", "contacted", "qualified", "won", "lost"];
const REPLY_TEMPLATES = [
  { id: "quote_sent", label: "Quote sent" },
  { id: "quote_preparing", label: "Quote in progress" },
  { id: "call_scheduled", label: "Schedule a call" },
  { id: "more_info", label: "Request more info" },
  { id: "followup", label: "Follow up" },
];
const date = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

function ReplySelect({ row }) {
  const [tpl, setTpl] = useState(null);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const open = (id) => {
    const found = REPLY_TEMPLATES.find((t) => t.id === id);
    if (found) { setTpl(found); setNote(""); }
  };

  const send = async () => {
    setSending(true);
    try {
      await api.post("/admin/reply", { email: row.email, name: row.name || "", template: tpl.id, note });
      toast.success(`Reply sent to ${row.email}`);
      setTpl(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <>
      <select value="" disabled={!row.email} onChange={(e) => open(e.target.value)} data-testid={`reply-select-${row.id}`}
        className="rounded-md border border-teal/40 bg-teal/5 px-2 py-1 text-xs font-medium text-teal outline-none focus:border-teal disabled:opacity-50">
        <option value="" disabled>✉ Reply…</option>
        {REPLY_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
      </select>
      {tpl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="reply-modal">
          <div className="w-full max-w-md space-y-4 rounded-xl bg-white p-6 shadow-2xl">
            <div>
              <h3 className="font-heading text-base font-semibold text-ink">Send "{tpl.label}"</h3>
              <p className="mt-1 text-xs text-slate-500">To: {row.name ? `${row.name} — ` : ""}{row.email}</p>
            </div>
            <textarea
              rows={3} autoFocus value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Add a personal note (optional) — it appears at the top of the email"
              data-testid="reply-note"
              className="w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30"
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setTpl(null)} disabled={sending} data-testid="reply-cancel"
                className="rounded-md px-4 py-2 text-sm font-medium text-slate-500 hover:text-ink">Cancel</button>
              <button onClick={send} disabled={sending} data-testid="reply-send"
                className="rounded-md bg-gradient-to-r from-[#1E73BE] to-[#16B894] px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {sending ? "Sending…" : "Send reply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function WaButton({ row }) {
  const phone = (row.phone || "").replace(/\D/g, "");
  if (!phone) return null;
  const msg = encodeURIComponent(`Hi ${row.name || "there"}, this is Unicorn Valley Group following up on your inquiry.`);
  return (
    <a href={`https://wa.me/${phone}?text=${msg}`} target="_blank" rel="noopener noreferrer"
      data-testid={`wa-lead-${row.id}`} title={`WhatsApp ${row.phone}`}
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#25D366] text-white transition-opacity hover:opacity-85">
      <WhatsappLogo size={16} weight="fill" />
    </a>
  );
}

function StatusSelect({ row, onChange }) {
  return (
    <select value={row.status || "new"} onChange={(e) => onChange(row.id, e.target.value)} data-testid={`status-select-${row.id}`}
      className="rounded-md border border-slate-200 px-2 py-1 text-xs capitalize outline-none focus:border-teal">
      {LEAD_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

const makeLeadColumns = (onStatusChange) => [
  { key: "name", label: "Name" }, { key: "email", label: "Email" },
  { key: "company", label: "Company" }, { key: "source", label: "Source", render: (r) => <span className="rounded bg-mist px-2 py-0.5 text-xs capitalize">{r.source}</span> },
  { key: "message", label: "Message", render: (r) => <span className="line-clamp-1 max-w-xs">{r.message || "—"}</span> },
  { key: "status", label: "Status", render: (r) => <StatusSelect row={r} onChange={onStatusChange} /> },
  { key: "reply", label: "Outreach", render: (r) => <div className="flex items-center gap-2"><ReplySelect row={r} /><WaButton row={r} /></div> },
  { key: "created_at", label: "Date", render: (r) => date(r.created_at) },
];

const makeQuoteColumns = (onStatusChange) => [
  { key: "name", label: "Name" }, { key: "email", label: "Email" },
  { key: "destination", label: "Destination" }, { key: "quantity", label: "Qty" },
  { key: "product_details", label: "Product", render: (r) => <span className="line-clamp-1 max-w-xs">{r.product_details}</span> },
  { key: "status", label: "Status", render: (r) => <StatusSelect row={r} onChange={onStatusChange} /> },
  { key: "reply", label: "Outreach", render: (r) => <div className="flex items-center gap-2"><ReplySelect row={r} /><WaButton row={r} /></div> },
  { key: "created_at", label: "Date", render: (r) => date(r.created_at) },
];

const SUPPLIER_COLUMNS = [
  { key: "name", label: "Name" }, { key: "email", label: "Email" },
  { key: "supplier_name", label: "Supplier" },
  { key: "supplier_contact", label: "Contact/URL" },
  { key: "product", label: "Product", render: (r) => <span className="line-clamp-1 max-w-xs">{r.product || "—"}</span> },
  { key: "created_at", label: "Date", render: (r) => date(r.created_at) },
];

const AUDIT_COLUMNS = [
  { key: "name", label: "Name" }, { key: "email", label: "Email" },
  { key: "factory_name", label: "Factory" }, { key: "factory_location", label: "Location" },
  { key: "audit_type", label: "Audit type" },
  { key: "created_at", label: "Date", render: (r) => date(r.created_at) },
];

const CONSULT_COLUMNS = [
  { key: "name", label: "Name" }, { key: "email", label: "Email" },
  { key: "topic", label: "Topic" }, { key: "preferred_date", label: "Preferred Date" },
  { key: "created_at", label: "Requested", render: (r) => date(r.created_at) },
];

const DOWNLOAD_COLUMNS = [
  { key: "name", label: "Name" }, { key: "email", label: "Email" },
  { key: "resource_title", label: "Resource" }, { key: "company", label: "Company" },
  { key: "created_at", label: "Date", render: (r) => date(r.created_at) },
];

function DataTable({ columns, rows, testid }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid={testid}>
      <table className="w-full text-left text-sm">
        <thead className="bg-mist text-xs uppercase tracking-wider text-slate-500">
          <tr>{columns.map((c) => <th key={c.key} className="px-5 py-3 font-semibold">{c.label}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={r.id ?? i} className="hover:bg-mist/50">
              {columns.map((c) => <td key={c.key} className="px-5 py-3.5 text-slate-700">{c.render ? c.render(r) : (r[c.key] || "—")}</td>)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={columns.length} className="px-5 py-10 text-center text-slate-400">No records yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function OverviewStats({ stats }) {
  const cards = [
    { label: "Total Leads", value: stats.leads, icon: Users },
    { label: "New Leads", value: stats.new_leads, icon: Users },
    { label: "Quote Requests", value: stats.quotes, icon: Package },
    { label: "Consultations", value: stats.consultations, icon: CalendarCheck },
    { label: "Downloads", value: stats.downloads, icon: DownloadSimple },
    { label: "Blog Posts", value: stats.blog_posts, icon: Article },
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {cards.map((c) => (
        <div key={c.label} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-6" data-testid={`admin-stat-${c.label}`}>
          <div className="flex h-12 w-12 items-center justify-center rounded-md bg-gradient-to-br from-[#1E73BE] to-[#16B894] text-white"><c.icon size={24} weight="duotone" /></div>
          <div><p className="font-heading text-3xl font-bold text-ink">{c.value ?? 0}</p><p className="text-xs text-slate-500">{c.label}</p></div>
        </div>
      ))}
    </div>
  );
}

function Sidebar({ tab, setTab, logout }) {
  return (
    <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col bg-ink lg:flex">
      <Link to="/" className="flex items-center gap-3 px-6 py-6">
        <div className="overflow-hidden rounded-md bg-[#EEF2FB] p-1"><img src={BRAND.logo} alt="logo" className="h-8 w-8 object-contain" /></div>
        <span className="font-heading text-sm font-bold text-white">UVG Admin</span>
      </Link>
      <nav className="flex-1 space-y-1 px-3">
        {NAV.map((n) => (
          <button
            key={n.id}
            onClick={() => setTab(n.id)}
            data-testid={`admin-nav-${n.id}`}
            className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
              tab === n.id ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            <n.icon size={20} weight="duotone" /> {n.label}
          </button>
        ))}
      </nav>
      <button onClick={logout} className="m-3 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-slate-400 hover:text-white" data-testid="admin-logout">
        <SignOut size={20} /> Logout
      </button>
    </aside>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [supplierChecks, setSupplierChecks] = useState([]);
  const [audits, setAudits] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [s, l, q, c, d, sv, fa] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/leads"), api.get("/admin/quote-requests"),
        api.get("/admin/consultations"), api.get("/admin/downloads"),
        api.get("/admin/supplier-verifications"), api.get("/admin/factory-audits"),
      ]);
      setStats(s.data); setLeads(l.data); setQuotes(q.data);
      setConsultations(c.data); setDownloads(d.data);
      setSupplierChecks(sv.data); setAudits(fa.data);
    } catch (e) { console.error("Failed to load admin data:", e); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const updateLeadStatus = useCallback(async (id, status) => {
    try { await api.patch(`/admin/leads/${id}`, { status }); toast.success("Status updated"); loadAll(); }
    catch { toast.error("Update failed"); }
  }, [loadAll]);

  const updateQuoteStatus = useCallback(async (id, status) => {
    try { await api.patch(`/admin/quote-requests/${id}`, { status }); toast.success("Status updated"); loadAll(); }
    catch { toast.error("Update failed"); }
  }, [loadAll]);

  const leadColumns = useMemo(() => makeLeadColumns(updateLeadStatus), [updateLeadStatus]);
  const quoteColumns = useMemo(() => makeQuoteColumns(updateQuoteStatus), [updateQuoteStatus]);

  return (
    <div className="flex min-h-screen bg-mist" data-testid="admin-dashboard">
      <Sidebar tab={tab} setTab={setTab} logout={logout} />

      <main className="flex-1 overflow-x-hidden">
        <div className="border-b border-slate-200 bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-xl font-semibold text-ink">{NAV.find((n) => n.id === tab)?.label}</h1>
              <p className="text-xs text-slate-500">Signed in as {user?.email}</p>
            </div>
            <select value={tab} onChange={(e) => setTab(e.target.value)} className="rounded-md border border-slate-200 px-3 py-2 text-sm lg:hidden" data-testid="admin-mobile-nav">
              {NAV.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
            </select>
          </div>
        </div>

        <div className="p-6">
          {tab === "overview" && <OverviewStats stats={stats} />}
          {tab === "analytics" && <AnalyticsDashboard />}
          {tab === "leads" && <DataTable testid="admin-leads-table" rows={leads} columns={leadColumns} />}
          {tab === "quotes" && <DataTable testid="admin-quotes-table" rows={quotes} columns={quoteColumns} />}
          {tab === "supplier" && <DataTable testid="admin-supplier-table" rows={supplierChecks} columns={SUPPLIER_COLUMNS} />}
          {tab === "audits" && <DataTable testid="admin-audits-table" rows={audits} columns={AUDIT_COLUMNS} />}
          {tab === "clients" && <ClientTracking />}
          {tab === "bookings" && <BookingsManager />}
          {tab === "consultations" && <DataTable testid="admin-consultations-table" rows={consultations} columns={CONSULT_COLUMNS} />}
          {tab === "downloads" && <DataTable testid="admin-downloads-table" rows={downloads} columns={DOWNLOAD_COLUMNS} />}
          {tab === "blog" && <BlogManager />}
          {tab === "casestudies" && <CaseStudiesManager />}
          {tab === "resources" && <ResourcesManager />}
          {tab === "testimonials" && <TestimonialsManager />}
          {tab === "categories" && <CategoriesManager />}
        </div>
      </main>
    </div>
  );
}
